import Sessions from "utils/Sessions";
import { createAsyncThunk } from "@reduxjs/toolkit";
import ApiService from "redux/ApiService/ApiService";
import { filterProjectStatusFunc, normalizeParamsAndAddValues } from "utils/methods/methods";

const getProjectProgressData = createAsyncThunk(
  "projectProgress/api",
  async (params, { signal }) => {
    const projectId = typeof params === "object" ? params?.projectId || params?.project : params;
    const requestSignal = typeof params === "object" ? params?.signal || signal : signal;
    const endPoint = `reports/project-progress-calculation?project=${projectId}`;
    const res = await ApiService.get(endPoint, {
      headers: { Authorization: `Bearer ${Sessions.userToken}` },
      signal: requestSignal,
    });
    return res;
  }
);

export const getReportProgressData = createAsyncThunk(
  "reportProgress/api",
  async ({ projectId, reportId, signal }, { signal: thunkSignal }) => {
    const endPoint = `reports/progress?projectId=${projectId}&reportId=${reportId}`;
    const res = await ApiService.get(endPoint, {
      headers: { Authorization: `Bearer ${Sessions.userToken}` },
      signal: signal || thunkSignal,
    });
    return res;
  }
);

export const getReportStatusData = createAsyncThunk(
  "reportStatus/api",
  async (params, { signal }) => {
    const projectId = typeof params === "object" ? params?.projectId || params?.project : params;
    const requestSignal = typeof params === "object" ? params?.signal || signal : signal;
    const endPoint = `reports/status-summary?projectId=${projectId}`;
    const res = await ApiService.get(endPoint, {
      headers: { Authorization: `Bearer ${Sessions.userToken}` },
      signal: requestSignal,
    });
    return res;
  }
);

export const getShiftStatusData = createAsyncThunk(
  "shiftStatus/api",
  async (params, { signal }) => {
    const projectId = typeof params === "object" ? params?.projectId || params?.project : params;
    const requestSignal = typeof params === "object" ? params?.signal || signal : signal;
    const endPoint = `shifts/count?project=${projectId}`;
    const res = await ApiService.get(endPoint, {
      headers: { Authorization: `Bearer ${Sessions.userToken}` },
      signal: requestSignal,
    });
    return res;
  }
);

export const getFilterProjects = createAsyncThunk("filterProjects/api", async (params) => {
  const projectStatus = filterProjectStatusFunc();
  const projectStatusObj = { status: projectStatus };

  const updatedParams = normalizeParamsAndAddValues(params, projectStatusObj);

  const res = await ApiService.get(`v2/projects?${updatedParams}`, {
    headers: { Authorization: `Bearer ${Sessions.userToken}` },
  });
  return res;
});

export const getFilterReports = createAsyncThunk(
  "filterReports/api",
  async (params, { signal }) => {
    // Getting id only in params so no need for project status.
    const projectId = typeof params === "object" ? params?.projectId || params?.project : params;
    const requestSignal = typeof params === "object" ? params?.signal || signal : signal;
    const res = await ApiService.get(`reports?project=${projectId}`, {
      headers: { Authorization: `Bearer ${Sessions.userToken}` },
      signal: requestSignal,
    });
    return res;
  }
);

export const getProjectBarChartData = createAsyncThunk(
  "projectDashboard/barChart",
  async ({ project, filters = {}, signal }, { rejectWithValue, signal: thunkSignal }) => {
    try {
      if (!project) {
        throw new Error("Project id is required");
      }

      const params = new URLSearchParams();
      params.set("project", project);

      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        const stringValue = typeof value === "number" ? value.toString() : `${value}`.trim();
        if (stringValue.length > 0) {
          params.set(key, stringValue);
        }
      });

      const endpoint = `projects/dashboard/bar-chart?${params.toString()}`;
      const res = await ApiService.get(endpoint, {
        headers: { Authorization: `Bearer ${Sessions.userToken}` },
        signal: signal || thunkSignal,
      });

      return res?.data;
    } catch (error) {
      const fallback = {
        message: error?.response?.data?.message || error?.message || "Unable to fetch chart data",
      };
      return rejectWithValue(error?.response?.data || fallback);
    }
  }
);

export const getProjectActivityHours = createAsyncThunk(
  "projectDashboard/activityHours",
  async (project, { rejectWithValue, signal }) => {
    try {
      const projectId =
        typeof project === "object" ? project?.projectId || project?.project : project;
      const requestSignal = typeof project === "object" ? project?.signal || signal : signal;

      if (!projectId) {
        throw new Error("Project id is required");
      }

      const endpoint = `projects/dashboard/activity-hours?project=${projectId}`;
      const res = await ApiService.get(endpoint, {
        headers: { Authorization: `Bearer ${Sessions.userToken}` },
        signal: requestSignal,
      });

      return res?.data;
    } catch (error) {
      const fallback = {
        message:
          error?.response?.data?.message || error?.message || "Unable to fetch pivot table data",
      };
      return rejectWithValue(error?.response?.data || fallback);
    }
  }
);

export default getProjectProgressData;
